import download from 'downloadjs';

import { prepareAndMakeRequest } from '@frontend/api';

type MaterialData = {
  additionalLeadTime?: number | null;
  cost?: number | null;
  customName?: string | null;
  units: string;
};

export const supplierMaterialsService = {
  create: (groupId: string, materialId: string, data: MaterialData) =>
    prepareAndMakeRequest.post(`/suppliers/${groupId}/materials`, {
      body: { ...data, materialId },
    }),

  delete: (supplierMaterialsId: string) =>
    prepareAndMakeRequest.delete(`/suppliers/materials/${supplierMaterialsId}`),

  downloadCsv: async (groupId: string) => {
    const response = await prepareAndMakeRequest.post<Blob>(
      `/suppliers/${groupId}/supplier_materials_csv_download`,
    );
    const blob = new Blob([response], {
      type: 'text/plain',
    });
    download(blob, 'supplier-materials.csv');
  },

  fetchMaterialInfo: (supplierId: string) =>
    prepareAndMakeRequest.get(`/suppliers/${supplierId}/private_materials`),

  fetchMaterialSelectorSettings: (supplierId: string) =>
    prepareAndMakeRequest.get(
      `/suppliers/${supplierId}/material_selector_settings`,
    ),

  list: (groupId: string, value: string) =>
    prepareAndMakeRequest.get(`/suppliers/${groupId}/materials`, {
      query: { search: value ?? '' },
    }),

  update: (supplierMaterialsId: string, data: MaterialData) =>
    prepareAndMakeRequest.patch(`/suppliers/materials/${supplierMaterialsId}`, {
      body: data,
    }),

  updateMaterialSelectorSettings: (
    supplierId: string,
    payload: {
      excludedMaterialClassIds?: string[];
      onlyIncludeSupplierMaterials?: boolean;
    },
  ) =>
    prepareAndMakeRequest.patch(
      `/suppliers/${supplierId}/material_selector_settings`,
      { body: payload },
    ),

  uploadCsv: (groupId: string, data: FormData) =>
    prepareAndMakeRequest.post(
      `/suppliers/${groupId}/supplier_materials_csv_upload`,
      { body: data },
    ),
};

export const globalMaterialsService = {
  downloadCsv: async () => {
    const response = await prepareAndMakeRequest.get<Blob>(
      '/materials/global_materials',
    );
    const blob = new Blob([response], {
      type: 'text/plain',
    });
    download(blob, 'global-materials.csv');
  },
};

export const stockRequest = {
  cylindrical: (
    provider: string,
    uuid: string,
    data: {
      barEnd: number;
      buffer: number;
      cutTolerance: number;
      cutWidth: number;
      diameter: number;
      length: number;
      materialId: string;
      quantity: number;
      submitType: string;
    },
  ) => {
    const {
      barEnd,
      buffer,
      cutTolerance,
      cutWidth,
      diameter,
      length,
      materialId,
      quantity,
      submitType,
    } = data;
    return prepareAndMakeRequest.post(
      `/materials/${provider}/cylindrical_stock_request/`,
      {
        body: {
          barEnd,
          buffer,
          cutTolerance,
          cutWidth,
          diameter,
          length,
          materialId,
          quantity,
          submitType,
          uuid,
        },
      },
    );
  },

  rectangular: (
    provider: string,
    uuid: string,
    data: {
      barEnd: number;
      buffer: number;
      cutTolerance: number;
      cutWidth: number;
      height: number;
      length: number;
      materialId: string;
      quantity: number;
      submitType: string;
      width: number;
    },
  ) => {
    const {
      barEnd,
      buffer,
      cutTolerance,
      cutWidth,
      height,
      length,
      materialId,
      quantity,
      submitType,
      width,
    } = data;
    return prepareAndMakeRequest.post(
      `/materials/${provider}/rectangular_stock_request/`,
      {
        body: {
          barEnd,
          buffer,
          cutTolerance,
          cutWidth,
          height,
          length,
          materialId,
          quantity,
          submitType,
          width,
          uuid,
        },
      },
    );
  },
  sheet: (
    provider: string,
    data: {
      edgeBuffer: string;
      materialId: string;
      partLength: number;
      partThickness: number;
      partWidth: number;
      thicknessTolerance: number;
    },
  ) => {
    const {
      edgeBuffer,
      materialId,
      partLength,
      partThickness,
      partWidth,
      thicknessTolerance,
    } = data;
    return prepareAndMakeRequest.post(`/materials/${provider}/sheets/`, {
      body: {
        edgeBuffer,
        materialId,
        partLength,
        partThickness,
        partWidth,
        thicknessTolerance,
      },
    });
  },
};

export const materialWidget = {
  getPartFilePacket: (uuid: string, queryParams: { partUuid: string }) =>
    prepareAndMakeRequest.get(`/materials/material_widget/${uuid}`, {
      query: queryParams,
    }),

  stockAvailability: (
    materialId: string,
  ): Promise<Record<string, { bar: boolean; rod: boolean; sheet: boolean }>> =>
    prepareAndMakeRequest.get<
      Record<string, { bar: boolean; rod: boolean; sheet: boolean }>
    >('/materials/stock_availability/', {
      query: { materialId },
    }),

  stockStatusCheck: (uuids: string[]) =>
    prepareAndMakeRequest.get('/materials/stock_statuses', {
      query: { uuids },
    }),

  stockInterrogation: (
    uuid: string,
    data: {
      stockTask: string;
      customInterrogationId: string;
      partUuid: string;
    },
  ) =>
    prepareAndMakeRequest.post(`/materials/stock_interrogation/${uuid}`, {
      body: data,
    }),

  nestingRequest: (
    uuid: string,
    data: {
      edgeBuffer: number;
      isManual: boolean;
      isSheetSizeManual: boolean;
      partBuffer: number;
      partLength: number;
      partWidth: number;
      sheetLength: number;
      sheetWidth: number;
    },
  ) => {
    const {
      edgeBuffer,
      isManual,
      isSheetSizeManual,
      partBuffer,
      partLength,
      partWidth,
      sheetLength,
      sheetWidth,
    } = data;
    return prepareAndMakeRequest.post(`/materials/nesting_request/${uuid}`, {
      body: {
        edgeBuffer,
        isManual,
        isSheetSizeManual,
        partBuffer,
        partLength,
        partWidth,
        sheetLength,
        sheetWidth,
      },
    });
  },
};

export const recordEvent = {
  navigateToVendor: (
    uuid: string,
    data: { stockType: string; vendorName: string },
  ) => {
    const { stockType, vendorName } = data;
    return prepareAndMakeRequest.post(`/materials/record_event/${uuid}`, {
      body: {
        stockType,
        vendorName,
      },
    });
  },
};
